import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, ArrowRight, Check, AlertCircle } from "lucide-react";import { createContact, formatApiError } from "@/lib/api";
import { CABINET } from "@/lib/content";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
    const [state, setState] = useState({ status: "idle", error: null });

    const submit = async (e) => {
        e.preventDefault();
        setState({ status: "sending", error: null });
        try {
            await createContact(form);
            setState({ status: "success", error: null });
            setForm({ name: "", email: "", phone: "", subject: "", message: "", website: "" });
        } catch (e) {
            setState({ status: "idle", error: formatApiError(e) });
        }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    return (
        <main data-testid="contact-page" style={{ paddingTop: 140 }}>
            <section style={{ padding: "40px 40px 120px" }}>
                <div className="mx-auto grid grid-cols-1 md:grid-cols-12 gap-16" style={{ maxWidth: 1400 }}>
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1 }} className="md:col-span-5">
                        <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Contact</div>
                        <div className="divider-terra mt-3" />
                        <h1 className="font-display mt-8" style={{ fontSize: "clamp(2.6rem, 6vw, 5rem)", lineHeight: 0.95, color: "var(--forest-dark)", fontWeight: 400, letterSpacing: "-0.015em" }}>
                            Écrire <span style={{ fontStyle: "italic", color: "var(--terra)" }}>au cabinet</span>.
                        </h1>
                        <p className="font-display mt-8" style={{ fontSize: "1.1rem", lineHeight: 1.65, color: "var(--ink-mid)", fontStyle: "italic", maxWidth: 420 }}>
                            Question médicale, demande de collaboration, coordination d'une prise en charge — chaque message reçoit une réponse dans les meilleurs délais.
                        </p>

                        <div className="mt-12 space-y-6" data-testid="contact-info">
                            <div className="flex items-start gap-4">
                                <MapPin size={18} style={{ color: "var(--terra)", marginTop: 4 }} />
                                <div>
                                    <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>Adresse</div>
                                    <div className="mt-2" style={{ fontSize: 15, lineHeight: 1.6, color: "var(--forest-dark)" }}>
                                        Rue Centrale 27<br />1880 Bex · Canton de Vaud
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Phone size={18} style={{ color: "var(--terra)", marginTop: 4 }} />
                                <div>
                                    <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>Téléphone</div>
                                    <a href={`tel:${CABINET.phoneIntl}`} className="mt-2 link-underline" style={{ fontSize: 15, color: "var(--forest-dark)", display: "block" }}>{CABINET.phone}</a>
                                    <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 4 }}>Lun–Ven · 08h30–18h00</div>
                                </div>
                            </div>
                            <div className="flex items-start gap-4">
                                <Mail size={18} style={{ color: "var(--terra)", marginTop: 4 }} />
                                <div>
                                    <div className="font-mono-clinical" style={{ color: "var(--forest)" }}>Email</div>
                                    <a href={`mailto:${CABINET.email}`} className="mt-2 link-underline" style={{ fontSize: 15, color: "var(--forest-dark)" }}>{CABINET.email}</a>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.15 }} className="md:col-span-7">
                        {state.status === "success" ? (
                            <SuccessContact onNew={() => setState({ status: "idle", error: null })} />
                        ) : (
                            <form onSubmit={submit} data-testid="contact-form" style={{ background: "var(--cream)", border: "1px solid var(--hairline)", padding: "36px", borderRadius: 4 }}>
                                {/* Honeypot */}
                                <input
                                    type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                                    value={form.website || ""} onChange={(e) => set("website", e.target.value)}
                                    style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
                                />
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div>
                                        <label className="field-label">Nom</label>
                                        <input required minLength={2} data-testid="contact-name" className="field-clinical" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Prénom Nom" />
                                    </div>
                                    <div>
                                        <label className="field-label">Téléphone (optionnel)</label>
                                        <input data-testid="contact-phone" className="field-clinical" value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="079 XXX XX XX" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="field-label">Adresse email</label>
                                        <input required type="email" data-testid="contact-email" className="field-clinical" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="votre@email.ch" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="field-label">Objet</label>
                                        <input data-testid="contact-subject" className="field-clinical" value={form.subject} onChange={(e) => set("subject", e.target.value)} placeholder="Objet du message" />
                                    </div>
                                    <div className="md:col-span-2">
                                        <label className="field-label">Message</label>
                                        <textarea required minLength={5} data-testid="contact-message" rows={5} className="field-clinical" value={form.message} onChange={(e) => set("message", e.target.value)} placeholder="Votre message…" style={{ resize: "vertical", fontFamily: "var(--font-body)" }} />
                                    </div>
                                </div>

                                {state.error && (
                                    <div className="mt-6 flex items-start gap-2" style={{ background: "rgba(198, 68, 68, 0.08)", padding: "12px 16px", borderRadius: 4, fontSize: 13, color: "#a63333" }}>
                                        <AlertCircle size={14} style={{ marginTop: 2 }} />
                                        <span>{String(state.error)}</span>
                                    </div>
                                )}

                                <div className="mt-10 flex items-center justify-end">
                                    <button type="submit" data-testid="contact-submit" disabled={state.status === "sending"} className="btn-primary" style={{ opacity: state.status === "sending" ? 0.5 : 1 }}>
                                        {state.status === "sending" ? "Envoi…" : "Envoyer le message"}<ArrowRight size={16} />
                                    </button>
                                </div>
                            </form>
                        )}
                    </motion.div>
                </div>
            </section>

            {/* Map section — inspired by Physiothérapie Les Mains Nuages */}
            <section data-testid="contact-map-section" style={{ padding: "0 40px 120px" }}>
                <div className="mx-auto" style={{ maxWidth: 1400 }}>
                    <div className="font-mono-clinical" style={{ color: "var(--terra)" }}>Nous rendre visite</div>
                    <div className="divider-terra mt-3" />
                    <h2 className="font-display mt-8" style={{ fontSize: "clamp(2rem, 4vw, 3.2rem)", lineHeight: 1, color: "var(--forest-dark)", fontWeight: 400 }}>
                        Le cabinet est <span style={{ fontStyle: "italic", color: "var(--terra)" }}>facile d'accès</span>.
                    </h2>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-6">
                        <div className="md:col-span-8">
                            <div style={{ position: "relative", aspectRatio: "16/9", borderRadius: 4, overflow: "hidden", border: "1px solid var(--hairline)" }}>
                                <iframe
                                    data-testid="contact-map-iframe"
                                    title="Carte du cabinet ANA Corps & Souffle"
                                    src="https://www.openstreetmap.org/export/embed.html?bbox=6.995%2C46.245%2C7.030%2C46.262&layer=mapnik&marker=46.2515%2C7.0114"
                                    style={{ width: "100%", height: "100%", border: 0 }}
                                    loading="lazy"
                                    referrerPolicy="no-referrer-when-downgrade"
                                />
                            </div>
                        </div>
                        <div className="md:col-span-4" style={{ background: "var(--forest-deep)", color: "var(--cream)", padding: 32, borderRadius: 4, position: "relative", overflow: "hidden" }}>
                            <div aria-hidden className="breathe" style={{ position: "absolute", right: -60, top: -60, width: 200, height: 200, borderRadius: "50%", border: "1px solid rgba(216, 155, 146, 0.2)" }} />
                            <div className="font-mono-clinical" style={{ color: "var(--terra-light)" }}>Accès</div>
                            <div className="mt-3" style={{ height: 1, width: 40, background: "var(--terra)" }} />
                            <div className="font-display mt-6" style={{ fontSize: 22, color: "var(--cream)" }}>Rue Centrale 27</div>
                            <div style={{ fontSize: 15, color: "rgba(251,248,243,0.85)", marginTop: 4 }}>1880 Bex · Vaud · Suisse</div>
                            <ul style={{ listStyle: "none", padding: 0, margin: "24px 0 0", fontSize: 13.5, lineHeight: 1.7, color: "rgba(251,248,243,0.82)" }}>
                                <li>· Gare CFF de Bex — 8 min à pied</li>
                                <li>· Parking en zone bleue devant le cabinet</li>
                                <li>· Accessible en fauteuil roulant</li>
                                <li>· Consultation sur rendez-vous — lundi au vendredi</li>
                            </ul>
                            <a
                                href="https://www.openstreetmap.org/?mlat=46.2515&mlon=7.0114#map=17/46.2515/7.0114"
                                target="_blank" rel="noopener noreferrer"
                                data-testid="contact-map-directions"
                                className="btn-primary mt-8 inline-flex"
                                style={{ background: "var(--terra)", borderColor: "var(--terra)" }}
                            >
                                Itinéraire <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </main>
    );
}

function SuccessContact({ onNew }) {
    return (
        <div data-testid="contact-success" style={{ background: "var(--forest-deep)", color: "var(--cream)", padding: "48px", borderRadius: 4, position: "relative", overflow: "hidden" }}>
            <div className="breathe" style={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(216, 155, 146, 0.2)", border: "1px solid var(--terra)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--terra-light)" }}>
                <Check size={24} />
            </div>
            <h2 className="font-display mt-8" style={{ fontSize: "2.4rem", lineHeight: 1.1, fontWeight: 400 }}>
                Message <span style={{ fontStyle: "italic", color: "var(--terra-light)" }}>reçu</span>.
            </h2>
            <p className="font-display mt-6" style={{ fontSize: "1.05rem", lineHeight: 1.6, color: "rgba(251, 248, 243, 0.85)", maxWidth: 480 }}>
                Merci pour votre message. Ana vous répondra sous 48 heures ouvrables.
            </p>
            <button onClick={onNew} className="btn-ghost mt-8" style={{ color: "var(--cream)", borderColor: "rgba(255,255,255,0.3)" }} data-testid="contact-new">Écrire un autre message</button>
        </div>
    );
}
